import { type Coordinates } from 'react-advanced-cropper';
import auditService from '../services/auditService';

export const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (e: Event) => reject(e));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
};

const getRadianAngle = (degreeValue: number) => (degreeValue * Math.PI) / 180;

export const getCroppedImageFile = async (params: {
  previewUrl: string;
  selectedFile: File | null;
  rotation: number;
  coordinates: Coordinates | null;
}): Promise<File> => {
  const { previewUrl, selectedFile, rotation, coordinates } = params;
  if (!previewUrl) throw new Error('No image available');

  const hasSelected = Boolean(selectedFile);
  const baseFile = hasSelected
    ? (selectedFile as File)
    : new File([await auditService.getBlobFromUrl(previewUrl)], 'existing-receipt.jpg', { type: 'image/jpeg' });

  const image = await createImage(previewUrl);
  const imageWidth = image.naturalWidth || image.width;
  const imageHeight = image.naturalHeight || image.height;

  if (!coordinates) {
    return baseFile;
  }

  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const isNoRotation = normalizedRotation === 0;

  if (
    isNoRotation &&
    Math.abs(coordinates.left) < 1 &&
    Math.abs(coordinates.top) < 1 &&
    Math.abs(coordinates.width - imageWidth) < 1 &&
    Math.abs(coordinates.height - imageHeight) < 1
  ) {
    return baseFile;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return baseFile;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  canvas.width = Math.round(coordinates.width);
  canvas.height = Math.round(coordinates.height);

  if (isNoRotation) {
    ctx.drawImage(
      image,
      coordinates.left,
      coordinates.top,
      coordinates.width,
      coordinates.height,
      0,
      0,
      coordinates.width,
      coordinates.height,
    );
  } else {
    const rotRad = getRadianAngle(rotation);
    const cos = Math.abs(Math.cos(rotRad));
    const sin = Math.abs(Math.sin(rotRad));
    const rotatedWidth = imageWidth * cos + imageHeight * sin;
    const rotatedHeight = imageWidth * sin + imageHeight * cos;

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d', { alpha: false });
    if (!tempCtx) return baseFile;

    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';

    tempCanvas.width = rotatedWidth;
    tempCanvas.height = rotatedHeight;
    tempCtx.fillStyle = 'white';
    tempCtx.fillRect(0, 0, rotatedWidth, rotatedHeight);
    tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
    tempCtx.rotate(rotRad);
    tempCtx.drawImage(image, -imageWidth / 2, -imageHeight / 2);

    const centerX = rotatedWidth / 2;
    const centerY = rotatedHeight / 2;
    const cropCenterX = coordinates.left + coordinates.width / 2;
    const cropCenterY = coordinates.top + coordinates.height / 2;
    const rotatedCropX =
      centerX + (cropCenterX - imageWidth / 2) * cos - (cropCenterY - imageHeight / 2) * sin;
    const rotatedCropY =
      centerY + (cropCenterX - imageWidth / 2) * sin + (cropCenterY - imageHeight / 2) * cos;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, coordinates.width, coordinates.height);
    ctx.drawImage(
      tempCanvas,
      rotatedCropX - coordinates.width / 2,
      rotatedCropY - coordinates.height / 2,
      coordinates.width,
      coordinates.height,
      0,
      0,
      coordinates.width,
      coordinates.height,
    );
  }

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const croppedFile = new File([blob], baseFile.name, {
            type: baseFile.type,
            lastModified: Date.now(),
          });
          resolve(croppedFile);
        } else {
          resolve(baseFile);
        }
      },
      baseFile.type,
      0.95,
    );
  });
};
